import { createFileRoute } from "@tanstack/react-router"

import { Icon } from "@/components/icon/Icon"
import { Reveal } from "@/components/reveal/Reveal"
import { Seo } from "@/components/seo/Seo"
import { DELETION_TURNAROUND, SUPPORT_EMAIL } from "@/constants/company"

export const Route = createFileRoute("/data-deletion")({
	component: DataDeletion,
})

function DataDeletion() {
	return (
		<div className="flex flex-col">
			<Seo
				title="Data & Account Deletion"
				description="How to request deletion of your Swamithra account and personal data, and what gets removed."
				path="/data-deletion"
			/>
			<section className="bg-surface-container-low py-space-xl w-full">
				<div className="px-margin mx-auto max-w-7xl">
					<span className="mb-space-xs bg-surface-container px-space-sm py-space-xs text-label-sm text-primary inline-block rounded font-sans font-semibold tracking-widest uppercase">
						Legal &amp; Regulatory Compliance
					</span>
					<h1 className="text-display-lg text-on-surface font-serif tracking-tight">
						Data &amp; Account Deletion
					</h1>
					<p className="mt-space-sm text-body-md text-on-surface-variant max-w-[68ch] font-sans leading-relaxed">
						You can ask us to delete your Swamithra account and the personal data
						associated with it at any time.
					</p>
				</div>
			</section>

			<section className="py-space-xl w-full">
				<div className="px-margin gap-space-xl mx-auto flex max-w-3xl flex-col">
					<Reveal className="gap-space-sm border-hairline/70 bg-surface-container-lowest p-space-lg flex flex-col rounded-xl border shadow-sm">
						<div className="gap-space-sm text-primary flex items-center">
							<Icon
								name="how_to_reg"
								size={22}
							/>
							<h2 className="text-headline-md text-on-surface font-serif font-semibold">
								How to request
							</h2>
						</div>
						<p className="text-body-md text-on-surface-variant font-sans leading-relaxed">
							In the app, go to Profile → Delete my account. Or email us at{" "}
							<a
								href={`mailto:${SUPPORT_EMAIL}`}
								className="text-primary hover:underline"
							>
								{SUPPORT_EMAIL}
							</a>{" "}
							from your registered email or with your registered WhatsApp number,
							subject &ldquo;Account &amp; Data Deletion Request&rdquo;. We verify
							against your registered mobile number and action it within{" "}
							{DELETION_TURNAROUND}.
						</p>
					</Reveal>

					<Reveal
						delay={80}
						className="gap-space-sm border-hairline/70 bg-surface-container-lowest p-space-lg flex flex-col rounded-xl border shadow-sm"
					>
						<div className="gap-space-sm text-tertiary flex items-center">
							<Icon
								name="delete_sweep"
								size={22}
							/>
							<h2 className="text-headline-md text-on-surface font-serif font-semibold">
								What we delete
							</h2>
						</div>
						<ul className="gap-space-xs pl-space-md flex flex-col">
							<li className="gap-space-xs text-body-md text-on-surface flex items-start font-sans">
								<Icon
									name="check_circle"
									size={18}
									className="text-tertiary mt-1"
								/>
								<span>
									Your account and profile — name, mobile number, email, user
									type.
								</span>
							</li>
							<li className="gap-space-xs text-body-md text-on-surface flex items-start font-sans">
								<Icon
									name="check_circle"
									size={18}
									className="text-tertiary mt-1"
								/>
								<span>
									Your queries, vetting requests, messages and uploaded documents.
								</span>
							</li>
							<li className="gap-space-xs text-body-md text-on-surface flex items-start font-sans">
								<Icon
									name="check_circle"
									size={18}
									className="text-tertiary mt-1"
								/>
								<span>Your feedback and ratings.</span>
							</li>
						</ul>
					</Reveal>

					<Reveal
						delay={160}
						className="gap-space-sm bg-surface-container-high p-space-lg flex flex-col rounded-xl shadow-sm"
					>
						<div className="gap-space-sm text-primary flex items-center">
							<Icon
								name="policy"
								size={22}
							/>
							<h2 className="text-headline-md text-on-surface font-serif font-semibold">
								What we may retain
							</h2>
						</div>
						<p className="text-body-md text-on-surface-variant font-sans leading-relaxed">
							Limited information required for legitimate reasons such as security,
							fraud prevention or legal compliance, only for as long as necessary, and
							protected as described in our{" "}
							<a
								href="/privacy"
								className="text-primary hover:underline"
							>
								Privacy Policy
							</a>
							.
						</p>
					</Reveal>
				</div>
			</section>
		</div>
	)
}
